import React, {useState} from 'react';
import {Box, Button, Modal} from '@mui/material';
import PaymentInformation from '../../invoice/PaymentInformation';
import {money} from './record-math';
import {customerInvoiceTotals} from './invoice-math';
import {FooterCard, SignatureStep, TotalRow} from './EstimateFooter';

/*
 * The customer's view of a record (SQ-337), shared by the staff page's
 * Preview and the customer's own link: the totals card and the one action
 * at the foot. An estimate shows its total alone and, while it waits for the
 * customer, Confirm, which opens the drawn signature. An invoice or receipt
 * shows Total, Amount Paid and Balance Due (or Overpaid), and Pay while a
 * balance is due, which shows the company's payment information. Never a
 * job's: customers have no jobs.
 */

/** The estimate's one line: what the customer is agreeing to. */
export const CustomerEstimateTotal = ({total}: {total: number}) => (
  <div className="er-info er-job-total">
    <TotalRow strong label="Estimate Total" value={money(total)} amount={total}/>
  </div>
);

/** The invoice's (or receipt's) lines: Total, Amount Paid, Balance Due or Overpaid. */
export const CustomerInvoiceTotals = ({total, paid, status}: {total: number; paid: number; status: string}) => {
  const {lines} = customerInvoiceTotals(total, paid, status);
  return (
    <div className="er-info er-job-total">
      {lines.map(line => (
        <TotalRow key={line.label} strong={line.label === 'Total'} label={line.label} value={money(line.amount)} amount={line.amount}/>
      ))}
    </div>
  );
};

/** Pay: the company's mailing address, who checks are made out to and how to reach them. */
export const PayBlock = ({companyId}: {companyId: number}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <FooterCard>
        <button type="button" className="er-filled" onClick={() => setOpen(true)}>Pay</button>
      </FooterCard>
      {/* Drawn outside the record (a portal): MUI's own styles, not the record's. */}
      <Modal open={open} onClose={() => setOpen(false)} aria-label="Payment Information">
        <Box sx={{outline: 'none', px: 2}}>
          <PaymentInformation companyId={companyId}/>
          <Box sx={{display: 'flex', justifyContent: 'center', mt: -2}}>
            <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

/**
 * Confirm, then the signature: drawing it and Sign and Confirm approves the
 * estimate. [onSign] resolves once the server has it; Not now folds it away.
 */
export const ConfirmBlock = ({working, onSign}: {working: boolean; onSign: (file: File) => void}) => {
  const [signing, setSigning] = useState(false);
  return signing
    ? <SignatureStep working={working} onSign={onSign} onNotNow={() => setSigning(false)}/>
    : (
      <FooterCard>
        <button type="button" className="er-filled" disabled={working} onClick={() => setSigning(true)}>Confirm</button>
      </FooterCard>
    );
};
